
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useCurrentRentalPoint } from "@/stores/useCurrentRentalPoint";
import { useClientData } from "@/domains/clients/hooks";
import { useRentalKitsUnified } from '@/hooks/rentals/useRentalKitsUnified';
import { useRentalCreation } from "@/hooks/rentals/useRentalCreation";
import { useInventoryUnified } from "@/hooks/useInventoryUnified";
import { useRentalFormState, RentalFormData } from "./useRentalFormState";
import { useRentalInventory } from "./useRentalInventory";
import { useRentalValidation } from "./useRentalValidation";
import { useRentalCalculations } from "@/hooks/useRentalCalculations";
import { useTariffsUnified } from "@/domains/tariffs/hooks/useTariffsUnified";

interface UseAddRentalDialogProps {
  onSave?: (data: unknown) => void;
  onSuccess?: () => void;
  onClose: () => void;
}

export function useAddRentalDialog({ onSave, onSuccess, onClose }: UseAddRentalDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedKits, setSelectedKits] = useState<string[]>([]);
  
  const { toast } = useToast();
  const { currentRentalPoint } = useCurrentRentalPoint();
    const { clients, isLoading: clientsLoading } = useClientData();
  const { kits, isLoading: kitsLoading } = useRentalKitsUnified();
  const { createRental, loading: isCreating } = useRentalCreation();
  const { inventory: availableInventory, loading: inventoryLoading } = useInventoryUnified();
  const { tariffs } = useTariffsUnified();

  // Используем специализированные хуки
  const { formData, handleChange, resetForm } = useRentalFormState();
  const { 
    lastAction, 
    handleInventoryItemChange, 
    handleInventoryItemRemove, 
    handleInventoryItemReplace, 
    handleUndoLastAction 
  } = useRentalInventory();
  const { validationErrors, validateForm, clearValidationErrors } = useRentalValidation();
  const selectedInventoryItems = availableInventory.filter(item => 
    formData.inventoryItems.some(selected => selected.id === item.id)
  );

  // Находим выбранный тариф
  const selectedTariff = tariffs?.find(t => t.id === formData.tariffId);
  const tariffType = selectedTariff?.type || 'daily';

  const { totalAmount } = useRentalCalculations({
    inventoryItems: selectedInventoryItems,
    kits: kits,
    selectedKitIds: selectedKits,
    startDate: formData.startDate,
    endDate: formData.endDate,
    discount: formData.discount,
    tariffType, // передаем тип тарифа
  });

  const handleFieldChange = <K extends keyof RentalFormData>(field: K, value: RentalFormData[K]) => {
    console.log('[useAddRentalDialog] Field change:', field, value);
    handleChange(field, value);
  };

  // Обработчик изменения инвентаря с синхронизацией данных формы
  const handleInventoryChange = (id: string, isSelected: boolean) => {
    console.log('[useAddRentalDialog] Inventory change:', id, isSelected);

    const currentItems = [...formData.inventoryItems];
    const existingItemIndex = currentItems.findIndex(item => item.id === id);

    if (isSelected) {
      if (existingItemIndex === -1) {
        // Добавляем только ID, т.к. quantity всегда 1
        currentItems.push({ id, quantity: 1 });
      }
    } else {
      if (existingItemIndex !== -1) {
        currentItems.splice(existingItemIndex, 1);
      }
    }

    // CRITICAL FIX: Auto-set rental dates when inventory items are selected (similar to orders)
    if (currentItems.length > 0) {
      // Если даты не установлены или установлены некорректно, устанавливаем дефолтные
      if (!formData.startDate || !formData.endDate || formData.endDate <= formData.startDate) {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        console.log('[useAddRentalDialog] Auto-set rental dates for inventory (handleInventoryChange):', {
          startDate: now,
          endDate: tomorrow,
          reason: !formData.startDate ? 'no start date' : !formData.endDate ? 'no end date' : 'invalid date range'
        });
        
        // Устанавливаем даты через handleChange для корректного обновления состояния
        handleChange('startDate', now);
        handleChange('endDate', tomorrow);
      }
    } else {
      console.log('[useAddRentalDialog] No inventory items selected (handleInventoryChange), keeping current dates');
    }

    handleChange('inventoryItems', currentItems);

    // Логика для UI, если потребуется
    if (isSelected) {
      handleInventoryItemChange(id, 1);
    } else {
      handleInventoryItemChange(id, 0);
    }
  };

  // Обработчик изменения количества инвентаря (для InventorySelectionSection)
  const handleInventoryItemQuantityChange = (id: string, quantity: number) => {
    console.log('[useAddRentalDialog] Inventory quantity change:', id, quantity);

    const currentItems = [...formData.inventoryItems];
    const existingItemIndex = currentItems.findIndex(item => item.id === id);

    if (quantity > 0) {
      if (existingItemIndex === -1) {
        // Добавляем новый элемент
        currentItems.push({ id, quantity });
      } else {
        // Обновляем количество существующего элемента
        currentItems[existingItemIndex] = { id, quantity };
      }
    } else {
      // Удаляем элемент если количество 0
      if (existingItemIndex !== -1) {
        currentItems.splice(existingItemIndex, 1);
      }
    }

    // CRITICAL FIX: Auto-set rental dates when inventory items are selected (similar to orders)
    if (currentItems.length > 0) {
      // Если даты не установлены или установлены некорректно, устанавливаем дефолтные
      if (!formData.startDate || !formData.endDate || formData.endDate <= formData.startDate) {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        console.log('[useAddRentalDialog] Auto-set rental dates for inventory:', {
          startDate: now,
          endDate: tomorrow,
          reason: !formData.startDate ? 'no start date' : !formData.endDate ? 'no end date' : 'invalid date range'
        });
        
        // Устанавливаем даты через handleChange для корректного обновления состояния
        handleChange('startDate', now);
        handleChange('endDate', tomorrow);
      }
    } else {
      console.log('[useAddRentalDialog] No inventory items selected, keeping current dates');
    }

    handleChange('inventoryItems', currentItems);
    handleInventoryItemChange(id, quantity);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('[useAddRentalDialog] Starting rental creation process');
    
    clearValidationErrors();

    const errors = validateForm(formData, selectedKits);
    if (errors.length > 0) {
      toast({
        title: "Ошибка валидации",
        description: errors.join(", "),
        variant: "destructive"
      });
      return;
    }

    if (!currentRentalPoint) {
      toast({
        title: "Ошибка",
        description: "Не выбран пункт проката",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const rentalData = {
        clientId: formData.clientId,
        rentalPointId: formData.rentalPointId || currentRentalPoint.id,
        returnPointId: formData.returnPointId || undefined,
        startDate: formData.startDate!,
        endDate: formData.endDate!,
        status: formData.status as 'pending' | 'active' | 'completed' | 'cancelled',
        notes: formData.notes,
        discount: formData.discount || 0,
        depositAmount: formData.depositAmount || 0,
        promoCode: formData.promoCode || '',
        tariffId: formData.tariffId || undefined,
        paymentMethodId: formData.paymentMethodId || undefined,
        paymentStatus: formData.paymentStatus || undefined,
        inventoryItems: formData.inventoryItems,
        selectedKits: selectedKits.map((id) => ({ id })),
        totalAmount
      };

      console.log('[useAddRentalDialog] About to create rental with data:', rentalData);
      
      const createdRental = await createRental(rentalData);
      
      console.log('[useAddRentalDialog] Rental created successfully:', createdRental);
      
      if (createdRental) {
        if (onSave) {
          onSave(createdRental);
        }
        
        if (onSuccess) {
          onSuccess();
        }
        
        resetForm();
        setSelectedKits([]);
        onClose();
        
        toast({
          title: "Успех",
          description: "Аренда успешно создана"
        });
      }
    } catch (error: unknown) {
      console.error('[useAddRentalDialog] Error creating rental:', error);
      const message = typeof error === 'object' && error && 'message' in error
        ? String((error as { message?: unknown }).message)
        : 'Не удалось создать аренду';
      toast({
        title: "Ошибка",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    selectedKits,
    setSelectedKits,
    lastAction,
    handleFieldChange,
    handleInventoryItemChange: handleInventoryItemQuantityChange,
    handleInventoryChange,
    handleUndoLastAction,
    handleSubmit,
    isSubmitting,
    validationErrors,
    clients,
    clientsLoading,
    currentRentalPoint,
    totalAmount,
    kits,
    kitsLoading,
    availableInventory,
    inventoryLoading
  };
}
